import React, { memo, useMemo } from 'react';
import { ServerCard } from './ServerCard';

// Props pour le ServerCard optimisé
interface OptimizedServerCardProps {
  rank?: number;
  name: string;
  description: string;
  ip: string;
  version: string;
  category: string;
  players: {
    online: number;
    max: number;
  };
  votes: number;
  tags: string[];
  isOnline: boolean;
  serverId: string;
  serverSlug: string;
  isSponsored?: boolean;
  sponsorTier?: string;
  customColor?: string | null;
  bannerImageUrl?: string | null;
  logoImageUrl?: string | null;
  prestigeExpiresAt?: string | null;
  isCompetitiveMode?: boolean;
  competitiveVotes?: number;
  competitiveRank?: number;
  rankChange?: number;
}

// Composant ServerCard mémorisé pour éviter les re-renders inutiles
export const OptimizedServerCard = memo<OptimizedServerCardProps>(({
  rank = 0,
  name,
  description,
  ip,
  version,
  category,
  players,
  votes,
  tags,
  isOnline,
  serverId,
  serverSlug,
  isSponsored = false,
  sponsorTier,
  customColor,
  bannerImageUrl,
  logoImageUrl,
  prestigeExpiresAt,
  isCompetitiveMode = false,
  competitiveVotes,
  competitiveRank,
  rankChange
}) => {
  // Mémoriser les props calculées pour éviter les recalculs
  const memoizedProps = useMemo(() => ({
    rank,
    name,
    description,
    ip,
    version,
    category,
    players,
    votes,
    tags,
    isOnline,
    serverId,
    serverSlug,
    isSponsored,
    sponsorTier,
    customColor,
    bannerImageUrl,
    logoImageUrl,
    prestigeExpiresAt,
    isCompetitiveMode,
    competitiveVotes,
    competitiveRank,
    rankChange
  }), [
    rank, name, description, ip, version, category, 
    players.online, players.max, votes, JSON.stringify(tags), 
    isOnline, serverId, serverSlug, isSponsored, sponsorTier,
    customColor, bannerImageUrl, logoImageUrl, prestigeExpiresAt,
    isCompetitiveMode, competitiveVotes, competitiveRank, rankChange
  ]);

  return <ServerCard {...memoizedProps} />;
}, (prevProps, nextProps) => {
  // Comparaison personnalisée pour éviter les re-renders inutiles
  return (
    prevProps.serverId === nextProps.serverId &&
    prevProps.votes === nextProps.votes &&
    prevProps.players.online === nextProps.players.online &&
    prevProps.isOnline === nextProps.isOnline &&
    prevProps.rank === nextProps.rank &&
    JSON.stringify(prevProps.tags) === JSON.stringify(nextProps.tags) &&
    prevProps.competitiveVotes === nextProps.competitiveVotes &&
    prevProps.competitiveRank === nextProps.competitiveRank
  );
});

OptimizedServerCard.displayName = 'OptimizedServerCard';